<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, onBeforeMount, onMounted, ref } from "vue"
import { useRoute } from "vue-router"
import { useI18n } from "vue-i18n"
import { CONSENT_REQUEST_STATUS, SOURCES_TYPES } from "@core/constants"
import { useAuthStore } from "@core/stores/auth.store"
import { coreServiceRegistry } from "@core/services/register"
import { formatDate } from "@core/helpers"
import type { ConsentRequestInvitationDto } from "@types"

// ================================
// ROUTE & STORES
// ================================

const route = useRoute()
const authStore = useAuthStore()

// ================================
// I18N
// ================================

const { t } = useI18n({
    messages: {
        fr: {
            /** Titre principal du parcours (aligné sur l’invitation signature côté structure). */
            page_title: "Partager l'accès aux données",
            title_expired: "Demande indisponible",
            title_refused: "Partage refusé",
            title_accepted: "Partage accepté",
            loading: "Récupération des informations, veuillez patienter...",
            created_busy: "Mise à jour de la demande, veuillez patienter...",
            accept_busy: "Mise à jour de la demande, veuillez patienter...",
            refuse_busy: "Mise à jour de la demande, veuillez patienter...",
            accepted:
                "La demande a bien été acceptée. Vous pouvez quitter cette page.",
            expired:
                "Le lien a expiré car la demande est terminée ou bien elle n'existe plus.",
            refused:
                "La demande a bien été refusée. Vous pouvez quitter cette page.",
            manual_intro:
                "{name} de la société {company} vous invite à partager vos données énergétique",
            meter_pdl: "Numéro du compteur d'électricité :",
            meter_pce: "Numéro du compteur de gaz :",
            address_label: "Adresse :",
            date_label: "Date de la demande :",
            share: "Partager mes données",
            refuse: "Refuser",
        },
    },
})

// ================================
// STATE
// ================================

type SharePhase =
    | "loading"
    | "created_busy"
    | "accept_busy"
    | "refuse_busy"
    | "manual"
    | "accepted"
    | "refused"
    | "expired"

const phase = ref<SharePhase>("loading")
const shareRequest = ref<ConsentRequestInvitationDto | null>(null)

// ================================
// COMPUTED
// ================================

const requestId = computed(() => String(route.params.id ?? ""))

const loadingLabel = computed(() => {
    if (phase.value === "created_busy") return t("created_busy")
    if (phase.value === "accept_busy") return t("accept_busy")
    if (phase.value === "refuse_busy") return t("refuse_busy")
    return t("loading")
})

const manualUserLine = computed(() => {
    const r = shareRequest.value
    if (!r?.user_from) return ""
    const uf = r.user_from
    const name = `${uf.firstname ?? ""} ${uf.lastname ?? ""}`.trim()
    const company = (uf.company as { name?: string } | undefined)?.name ?? ""
    return t("manual_intro", { name, company })
})

const manualRequestHtml = computed(() => {
    const r = shareRequest.value
    if (!r) return ""
    const isPdl = r.source?.idtype === SOURCES_TYPES.PDL
    const meterLabel = isPdl ? t("meter_pdl") : t("meter_pce")
    const idsource = r.source?.idsource ?? ""
    const street = r.building?.adress?.street ?? r.address?.street ?? ""
    const zip = r.building?.adress?.zipcode ?? r.address?.zipcode ?? ""
    const city = r.building?.adress?.city ?? r.address?.city ?? ""
    const addr = [street, zip, city].filter(Boolean).join(" ")
    const dateStr = r.createdAt ? formatDate(r.createdAt, "DD/MM/YYYY") : ""
    return `<strong>${meterLabel}</strong> ${idsource}<br><strong>${t("address_label")}</strong> ${addr}<br><strong>${t("date_label")}</strong> ${dateStr}`
})

// ================================
// LIFECYCLE
// ================================

onBeforeMount(() => {
    authStore.logout()
})

onMounted(() => {
    void loadShare()
})

// ================================
// FLOW
// ================================

async function loadShare(): Promise<void> {
    phase.value = "loading"
    const id = requestId.value
    const token =
        typeof route.query.token === "string" ? route.query.token : undefined
    const action =
        typeof route.query.action === "string" ? route.query.action : undefined

    try {
        const data =
            await coreServiceRegistry.consentRequest.getConsentRequestShare(
                id,
                token
            )

        if (data.token) {
            authStore.setToken(data.token)
            await authStore.fetchUser()
        }

        shareRequest.value = data
        const st = data.state

        if (
            st === CONSENT_REQUEST_STATUS.CREATED &&
            (action === "refused" || action === "accepted")
        ) {
            phase.value = "created_busy"
            const newStatus =
                action === "refused"
                    ? CONSENT_REQUEST_STATUS.REFUSED
                    : CONSENT_REQUEST_STATUS.TERMINATED
            try {
                await coreServiceRegistry.consentRequest.updateConsentRequestShare(
                    id,
                    newStatus
                )
                phase.value = action === "refused" ? "refused" : "accepted"
            } catch {
                phase.value = "expired"
            }
            return
        }

        if (st === CONSENT_REQUEST_STATUS.CREATED) {
            phase.value = "manual"
            return
        }

        if (
            st === CONSENT_REQUEST_STATUS.ACCEPTED ||
            st === CONSENT_REQUEST_STATUS.TERMINATED
        ) {
            phase.value = "accepted"
            return
        }

        if (st === CONSENT_REQUEST_STATUS.REFUSED) {
            phase.value = "refused"
            return
        }

        phase.value = "expired"
    } catch {
        phase.value = "expired"
    }
}

async function onAccept(): Promise<void> {
    const id = requestId.value
    phase.value = "accept_busy"
    try {
        await coreServiceRegistry.consentRequest.updateConsentRequestShare(
            id,
            CONSENT_REQUEST_STATUS.TERMINATED
        )
        phase.value = "accepted"
    } catch {
        phase.value = "expired"
    }
}

async function onRefuse(): Promise<void> {
    const id = requestId.value
    phase.value = "refuse_busy"
    try {
        await coreServiceRegistry.consentRequest.updateConsentRequestShare(
            id,
            CONSENT_REQUEST_STATUS.REFUSED
        )
        phase.value = "refused"
    } catch {
        phase.value = "expired"
    }
}
</script>

<template>
    <div
        class="page-consent-share-invitation flex min-h-screen w-full flex-col"
    >
        <ConsentRequestScopeHeader />
        <div class="flex flex-1 justify-center px-4 py-8">
            <div
                class="w-full max-w-xl flex flex-col items-center gap-6 text-center"
            >
                <div
                    v-if="
                        [
                            'loading',
                            'created_busy',
                            'accept_busy',
                            'refuse_busy',
                        ].includes(phase)
                    "
                    class="flex flex-col items-center gap-4 py-8"
                >
                    <BaseProgressSpinner style="width: 2rem; height: 2rem" />
                    <p class="text-sm text-gray-600">
                        {{ loadingLabel }}
                    </p>
                </div>

                <div
                    v-else-if="phase === 'expired'"
                    class="flex flex-col gap-3"
                >
                    <h1
                        class="text-xl font-semibold text-gray-800 flex items-center justify-center gap-2"
                    >
                        {{ t("title_expired") }}
                        <i
                            class="pi pi-info-circle text-gray-500"
                            aria-hidden="true"
                        />
                    </h1>
                    <p class="text-sm text-amber-900">
                        {{ t("expired") }}
                    </p>
                </div>

                <div
                    v-else-if="phase === 'refused'"
                    class="flex flex-col gap-3"
                >
                    <h1
                        class="text-xl font-semibold text-gray-800 flex items-center justify-center gap-2"
                    >
                        {{ t("title_refused") }}
                        <i
                            class="pi pi-info-circle text-gray-500"
                            aria-hidden="true"
                        />
                    </h1>
                    <p class="text-sm text-gray-700">
                        {{ t("refused") }}
                    </p>
                </div>

                <div
                    v-else-if="phase === 'accepted'"
                    class="flex flex-col gap-3"
                >
                    <h1
                        class="text-xl font-semibold text-gray-800 flex items-center justify-center gap-2"
                    >
                        {{ t("title_accepted") }}
                        <i
                            class="pi pi-info-circle text-gray-700"
                            aria-hidden="true"
                        />
                    </h1>
                    <p class="text-sm text-gray-700">
                        {{ t("accepted") }}
                    </p>
                </div>

                <div
                    v-else-if="phase === 'manual'"
                    class="flex flex-col gap-4 items-center w-full"
                >
                    <header
                        class="flex flex-col items-center gap-6 w-full pt-2 text-center"
                    >
                        <h1
                            class="text-xl font-semibold text-gray-800 text-center"
                        >
                            {{ t("page_title") }}
                        </h1>
                    </header>
                    <p class="text-sm text-gray-800">
                        {{ manualUserLine }}
                    </p>
                    <p
                        class="text-sm text-gray-700 text-center"
                        v-html="manualRequestHtml"
                    />
                    <div class="flex flex-row gap-6 mt-4">
                        <BaseButton
                            :label="t('refuse')"
                            severity="secondary"
                            @click="onRefuse"
                        />
                        <BaseButton :label="t('share')" @click="onAccept" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
